import { getUserData } from '../API';
import {
  FORCE_BAD_SIGNAL_AUDIO,
  FORCE_BAD_SIGNAL_CURSOR,
  FORCE_BAD_SIGNAL_LOG_ONLY,
  FORCE_BAD_SIGNAL_NETWORK,
} from '../../constants/featureFlags';
import {
  FLATLINE_MIN_RUN_SEC,
  PERVASIVE_PHRASE_SHARE,
  PERVASIVE_MIN_PHRASES,
} from '../../constants/audioDetectorThresholds';

/**
 * The tuning surface for the TT-991 bad-signal cursor branch: what counts as a "bad audio"
 * or "bad network" prior activity, which of TT-1230's audio branches cursor-free is allowed to
 * trust, where the WCPM pivot sits, and whether the branch is on.
 *
 * Data rather than logic, on purpose: a retune (TT-1230's audio boundaries, TT-1257's network
 * count, product's confirmation of the pivot) lands here as a value change instead of as a rewrite
 * of the decision path. Classification lives in cursor/priorActivitySignals and the behavior
 * choice in cursor/badSignalCursorPolicy. Classification takes every threshold it reads as an
 * argument, so it is testable against injected numbers; the policy takes the WCPM pivot as one
 * but imports BAD_SIGNAL_NETWORK_ENABLED, BAD_SIGNAL_REQUIRES_DISTRICT_THRESHOLD and
 * HIGH_PRECISION_BRANCHES directly, so those three are a retune point here, not an injection
 * point there.
 *
 * WHICH AUDIO BRANCHES EXIST IS NOT A VALUE HERE, AND IS SETTLED. Ka decided on 2026-09-01 that
 * hadBadAudio ships as TT-1230's dead silence and pervasive clipping; low volume and
 * clipped + unintelligible are not implemented, final. Nothing in this file is waiting on that.
 */

// TODO(TT-991): the ticket writes the pivot as "30?" — confirm with product before this
// ships to real districts. Pinned by a test so a retune is a deliberate edit, not a drift.
export const BAD_SIGNAL_WCPM_PIVOT = 30;

/**
 * How old a STORED prior-activity signal may be and still count, in days. The next-day path reads
 * the student's most recent scored tutor activity, which for a student returning from a long break
 * can be months old; a mic that was broken in March says nothing about September, and a mode
 * entered on that evidence has no automatic exit. Older than this the signal is reported to the
 * log as stale and classifies as unknown. The live (same-session) signal is never stale.
 *
 * Overridable like the pivot (badSignalMaxSignalAgeDays, district field first). A judgement call
 * rather than a measured number -- there is no analysis behind 30 -- and the log records the value
 * applied so a retune can be checked against the rows it would have changed.
 */
export const BAD_SIGNAL_MAX_SIGNAL_AGE_DAYS = 30;

// Whether the bad-signal cursor-free outcome still requires the district's configured
// cursorFreeTutorWCPMThreshold. False -- waived per product 2026-09-16: a detected bad signal
// resolves on the WCPM pivot alone. This spends the branch's only per-district cursor-free lever,
// since forceCursorFreeOff is read off appSettings (district 9999999) and never per district; PR
// #6029 has the consequences and the TODO for plumbing it properly.
export const BAD_SIGNAL_REQUIRES_DISTRICT_THRESHOLD = false;

/**
 * Whether hadBadNetwork may open the bad-signal branch at all. TRUE as of GT-306 (2026-10-06).
 *
 * THE RULE BEHIND IT IS TT-1377's, NOT badCount. hadBadNetwork is the student-level chronic
 * high-RTT verdict from cursor/networkHistorySignals — 2+ distinct high-RTT calendar days AND
 * >= 50% of the student's counted sessions with send→ack p95 >= 3s, computed once at launch from
 * the 10 tutor rows the login read already returns, with the session p95 taken as the max over its
 * phrases' ackP95Ms. Both definitions confirmed by Hriday on GT-306. badCount survives only as a
 * logged sanity check (badCountVerdict); TT-1257's placeholder count rule no longer feeds the
 * policy.
 *
 * Still gated by the badSignalCursorEnabled license flag like everything else in the branch, and
 * by forceCursorFreeOff / forceTapWordOff at the policy. The decision log carries the history the
 * verdict came from (steps.networkHistory), so a retune can be checked against the rows.
 *
 * This must stay checked INSIDE gate 1 of badSignalCursorPolicy: an early return on the switch
 * would disable the audio path too. CursorOrchestrator.badSignal.test.js imports the constant, so
 * its cross product flips with it; CursorOrchestrator.badNetwork.test.js forces it on regardless.
 */
export const BAD_SIGNAL_NETWORK_ENABLED = true;

/**
 * GT-306's one network branch, named like TT-1230's audio branches so the decision log records
 * which rule fired on the network side too. Only this branch exists; badCount is not a branch.
 */
export const NETWORK_BRANCH = Object.freeze({
  CHRONIC_HIGH_RTT: 'chronic high rtt',
});

/**
 * TT-1230's four audio branches, its names verbatim. The strings are the classifier's output keys
 * and the policy's lookup keys, so they are the vocabulary rather than labels for it.
 */
export const AUDIO_BRANCH = Object.freeze({
  DEAD_SILENCE: 'dead silence',
  PERVASIVE_CLIPPING: 'pervasive clipping',
  LOW_VOLUME: 'low volume',
  CLIPPED_UNINTELLIGIBLE: 'clipped + unintelligible',
});

/**
 * The high-precision branches — the only evidence trusted with cursor-free, which is sticky and
 * has no automatic exit. TT-1230 measured dead silence at 100% precision and pervasive clipping at
 * 67.6%; low volume (42.2%) and clipped + unintelligible (37.5%) are deliberately absent. The full
 * recall-tuned rule, any branch at 40.8%, still drives tap-to-read, which is reversible.
 *
 * DORMANT AS SHIPPED, and deliberately left in place. low volume and clipped + unintelligible are
 * both conjoined with a per-phrase story-word overlap, and Ka's FINAL decision on 2026-09-01 is
 * that neither branch is implemented — they classify null (see
 * cursor/priorActivitySignals.classifyAudioBranches), so every hadBadAudio:true that can occur
 * already comes from one of the two branches listed here and the gate in badSignalCursorPolicy
 * never rejects anything. The list stays because it is what makes the precision split reviewable:
 * a reader can see which evidence cursor-free is allowed to run on without reading the policy, and
 * a future branch is added to data rather than to an if.
 */
export const HIGH_PRECISION_BRANCHES = Object.freeze([
  AUDIO_BRANCH.DEAD_SILENCE,
  AUDIO_BRANCH.PERVASIVE_CLIPPING,
]);

/**
 * The three rule parameters that are applied during NORMALIZATION rather than classification, and
 * are therefore plain constants rather than per-district thresholds.
 *
 * The reason is parity, not taste. Each one cuts a PER-PHRASE (or per-segment) population that only
 * one of the two adapters ever sees: Speech.getActivityAudioMetrics() hands the live path counts it
 * has already cut, while TT-1256's stored payload hands the stored path the raw per-phrase
 * clippingP90 rows to cut itself. A tunable value here would mean the same activity classified
 * against one number today and another tomorrow, which is exactly what the shared normalized shape
 * exists to prevent. Everything applied to the counts AFTER that cut lives in
 * DEFAULT_AUDIO_THRESHOLDS below and stays overridable.
 *
 * All three are the shipped detector's own values, IMPORTED from constants/audioDetectorThresholds
 * rather than transcribed. That module is the single definition Speech.js and ReaderManager.js read
 * too, so drift is impossible rather than merely detected -- the pairing used to be a test that
 * read the source text and matched the literals, which could catch a retune but not prevent one.
 */
// Re-exported under this module's names so the classifier and its tests keep importing their
// thresholds from one place. LOW_VOLUME_SEGMENT_SHARE is ReaderManager's LOW_VOLUME_CHECK_THRESHOLD
// under the name the normalized shape uses: it is what raises the isLowVolume field TT-1256
// persists as a verdict, rather than a badness rule of its own.
export {
  CLIPPED_PHRASE_P90_PCT,
  PERVASIVE_PHRASE_P90_PCT,
} from '../../constants/audioDetectorThresholds';
export { LOW_VOLUME_CHECK_THRESHOLD as LOW_VOLUME_SEGMENT_SHARE } from '../../constants/audioDetectorThresholds';

/**
 * TT-1230's audio rule parameters — the boundaries applied at classification time.
 *
 * NOT PLACEHOLDERS ANY MORE, unlike the four borrowed activity-detector rules that used to live
 * here. Every value below is TT-1230's own, and every one is the same constant the shipped detector
 * reads, imported from constants/audioDetectorThresholds, so the same activity gets the same
 * verdict whether it is read live in-session or replayed off the student record:
 *
 *   flatlineMinRunSec     30    FLATLINE_MIN_RUN_SEC     — dead silence, 100% precision
 *   pervasivePhraseP90Pct  —    (see PERVASIVE_PHRASE_P90_PCT above, normalization-time)
 *   pervasivePhraseShare  0.5   PERVASIVE_PHRASE_SHARE   — pervasive clipping, 67.6%
 *   pervasiveMinPhrases   3     PERVASIVE_MIN_PHRASES    — that branch's evidence floor
 *
 * pervasiveMinPhrases is an evidence FLOOR rather than a badness rule, and it is #5953's addition
 * rather than anything TT-1230's prose mentions: without it one clipped phrase is 1/1 = 100% and
 * fires the "pervasive" verdict. The shipped isPervasivelyClipped is
 * `phraseClippingP90.length >= 3 && share >= 0.5`, so any derivation that drops the floor makes
 * the live and stored paths disagree on the same activity.
 *
 * WHAT WAS REMOVED, and why it is removal rather than retuning. minSegmentCount, lowRmsSegmentFrac,
 * staticLowBandSegmentFrac, staticBroadbandSegmentFrac and badClippedSegmentCount were the four
 * borrowed activity-detector boundaries plus their evidence floor, feeding a rule arm that ran
 * beside TT-1230's branches. TT-1230 §5 is a firm negative on that family — 35 feature/percentile
 * combinations swept with out-of-sample thresholds, best gain +0.008 Youden J, "nothing beyond
 * clipping and db improves the detector" — and §6 measured the badClippedSegments flag itself at
 * Youden J 0.189 and rejected it outright ("OR-ing it into the rule adds 9 false positives while
 * catching nothing new"). Keeping the keys would leave shipped thresholds no rule reads, which the
 * seam test in priorActivitySignals.test.js exists to forbid. lowRmsSegmentFrac's 0.90 survives as
 * LOW_VOLUME_SEGMENT_SHARE above; minSegmentCount is superseded by pervasiveMinPhrases, and could
 * not apply to the stored path in any case — TT-1256's payload carries no segment counts at all.
 */
export const DEFAULT_AUDIO_THRESHOLDS = Object.freeze({
  flatlineMinRunSec: FLATLINE_MIN_RUN_SEC,
  pervasivePhraseShare: PERVASIVE_PHRASE_SHARE,
  pervasiveMinPhrases: PERVASIVE_MIN_PHRASES,
});

/**
 * TT-1377's network rule parameters (GT-306), applied by cursor/networkHistorySignals to the tutor
 * rows the login read returns:
 *
 *   highRttP95Ms            3000  a SESSION is high-RTT when its send→ack p95 is >= this
 *   minHighRttDays             2  distinct calendar days with at least one high-RTT session
 *   minHighRttSessionShare   0.5  share of the student's counted sessions that were high-RTT
 *
 * Both of the last two must hold. Hriday's brief: dropping the share floor takes Click from 24
 * students to 245; tightening it to 75% mostly cuts Mellon (544 → 365); a 1-day rule is far larger
 * and a 3-day rule is tiny. 2 days and 50% is the recommended operating point, 617 / 3,137 flagged
 * students (19.7%) against 1.1% of matched controls and 1 student in the random sample.
 *
 *   badNetworkCount            2  TT-1257's finished-story episode counter
 *
 * NOT A TRIGGER ANY MORE. It is kept so the log's badCountVerdict sanity check still has a
 * boundary, and because mergeThresholds only copies keys the defaults declare, so dropping it
 * would also silently kill the appSettings override on it. It cannot see a quit, which is why the
 * brief keeps it as a check rather than a rule.
 *
 * Every key is overridable per district through badSignalNetworkThresholds, like the audio group.
 */
export const DEFAULT_NETWORK_THRESHOLDS = Object.freeze({
  badNetworkCount: 2,
  highRttP95Ms: 3000,
  minHighRttDays: 2,
  minHighRttSessionShare: 0.5,
});

/**
 * The values a district override of TT-1377's rule may take. Out of range keeps the default: with
 * minHighRttDays 0 or minHighRttSessionShare 0, every student with any timed history would be
 * flagged, and the outcome has no automatic exit. badNetworkCount is log-only and unchecked.
 */
const NETWORK_THRESHOLD_RANGES = Object.freeze({
  highRttP95Ms: (v) => v > 0,
  minHighRttDays: (v) => v >= 1,
  minHighRttSessionShare: (v) => v > 0 && v <= 1,
});

/**
 * The thresholds in effect for this student: in-code defaults overlaid per key with any
 * remote override, so a retune after TT-1230/TT-1257 needs no client release. Precedence
 * mirrors cursorFreeTutorWCPMThreshold — district-level field first, appSettings as
 * fallback. Non-numeric overrides are dropped rather than passed through, because a NaN or
 * undefined threshold would silently classify every activity the same way.
 *
 * Every key here is a license field on the AmiraLicenses record (badSignalAudioThresholds,
 * badSignalNetworkThresholds, badSignalWcpmPivot, badSignalMaxSignalAgeDays), mapped onto userData
 * by API.saveUserData once the SRS schema carries them. The threshold groups are accepted as
 * objects or as JSON text, since the schema has no typed field for them yet.
 *
 * @param {object} [userData] student/district config; defaults to the live userData store
 * @returns {{audio: object, network: object, wcpmPivot: number, maxSignalAgeDays: number}}
 */
export function getSignalThresholds(userData = getUserData()) {
  const config = userData || {};
  const pivotOverride = readOverride(config, 'badSignalWcpmPivot');
  const maxAgeOverride = readOverride(config, 'badSignalMaxSignalAgeDays');

  return {
    audio: mergeThresholds(DEFAULT_AUDIO_THRESHOLDS, readOverride(config, 'badSignalAudioThresholds')),
    network: mergeThresholds(
      DEFAULT_NETWORK_THRESHOLDS, readOverride(config, 'badSignalNetworkThresholds'), NETWORK_THRESHOLD_RANGES,
    ),
    wcpmPivot: Number.isFinite(pivotOverride) ? pivotOverride : BAD_SIGNAL_WCPM_PIVOT,
    maxSignalAgeDays: Number.isFinite(maxAgeOverride) ? maxAgeOverride : BAD_SIGNAL_MAX_SIGNAL_AGE_DAYS,
  };
}

/**
 * THE DARK LAUNCH, as a build constant. `true` means every district runs the branch and logs the
 * decision, and nobody's cursor changes; going live is a release that flips this to `false`, after
 * which the badSignalCursorEnabled license flag decides per district. Deliberately NOT a license
 * field or an appSettings key (Pete, 2026-09-10): the measurement wants every district at once,
 * and a remote switch would let one district be applying decisions it has no log of.
 *
 * Flipped to `false` for the go-live (TT-991): the dark-launch measurement is over, and the branch
 * now applies its decision only for districts whose badSignalCursorEnabled license flag is an exact
 * true (see isBadSignalCursorEnabled). No district with the flag unset is affected.
 *
 * Pinned by a test so flipping it is a deliberate edit rather than a drift. Overridable per
 * session by ?forceBadSignalLogOnly / ?forceBadSignalCursor=true (see isBadSignalLogOnly).
 */
export const BAD_SIGNAL_LOG_ONLY = false;

// The value isBadSignalLogOnly actually reads. Module state rather than the constant itself so the
// live-mode suites can exercise the post-dark-launch build without a second copy of the module;
// production never writes it.
let badSignalLogOnly = BAD_SIGNAL_LOG_ONLY;

/** Test seam only: stand in for the release that flips BAD_SIGNAL_LOG_ONLY. */
export function setBadSignalLogOnlyForTests(value) {
  badSignalLogOnly = value === true;
}

/**
 * TT-991's dark launch. With this on, a real production session runs the WHOLE branch — the
 * activity's signals are stashed, classified against the thresholds and put through the policy —
 * and then computeEligibleBehavior throws the behavior away instead of applying it. The student's
 * cursor is untouched; what comes out is the cursor_bad_signal_decision session log, saying who
 * WOULD have been sent to which mode.
 *
 * Log-only also OPENS the branch (see isBadSignalCursorEnabled) rather than modifying an already-
 * open one, so while the build is dark every district is in, and the release that flips
 * BAD_SIGNAL_LOG_ONLY hands control to the license. Nothing in userData is consulted.
 *
 * IT IS READ-ONLY ON THE STUDENT. rememberBadSignalBehavior stops at its in-memory session latch
 * and queues no SIS placement, so a dark launch leaves no per-student trace at all. A latch persisted
 * during a measurement would outlive it — the student would be in the mode the instant the switch
 * flipped, on evidence nobody had reviewed, and the district would go live with a population it
 * never decided on. A student the branch keeps choosing therefore appears as repeated `new`
 * decisions in the log rather than as one `latched` echo, which is the stronger evidence.
 *
 * PRECEDENCE, three levels. An explicit ?forceBadSignalLogOnly wins outright, so both halves of
 * the rollout stay reachable on one district config. Failing that, ?forceBadSignalCursor=true means
 * APPLY: that param exists so a dev can exercise the branch end to end, and on a district already
 * set to log-only it would otherwise open the branch and then withhold every outcome — the param
 * would look broken, and needing a second param to make the first one do anything is worse than a
 * name that carries both meanings. Failing both, the district setting decides.
 *
 * Both params are honoured on production too, like ?forceCursorFree. That is safe because a
 * param-driven session is a forced session (isBadSignalForcedSession) and writes nothing to the
 * student, so a URL can exercise the branch on a dark district without latching anyone.
 *
 * Takes no input: callers still pass userData for symmetry with isBadSignalCursorEnabled, and it is
 * ignored -- the dark launch reads nothing per district.
 *
 * @returns {boolean}
 */
export function isBadSignalLogOnly() {
  if (FORCE_BAD_SIGNAL_LOG_ONLY !== undefined) {
    return FORCE_BAD_SIGNAL_LOG_ONLY;
  }
  if (FORCE_BAD_SIGNAL_CURSOR === true) {
    return false;
  }
  return badSignalLogOnly;
}

/**
 * Whether this session is driven by TT-991's OWN debug params rather than by district config.
 *
 * Exists for one reason: both cursor modes are sticky with no exit — that is the product decision,
 * not a gap — so a QA run that latched its test student would strand them permanently. A forced
 * TEST session must leave no mark on the student.
 *
 * READS ONLY TT-991's PARAMS: ?forceBadSignalCursor, ?forceBadSignalLogOnly, ?forceDeadSilenceAudio,
 * ?forcePervasiveClippingAudio and GT-306's ?forceHighRttNetwork. ?forceCursorFree and ?forceTapToRead belong to the other two
 * cursor rollouts and whether those leave a mark is their owners' call. ?forceLowAudio,
 * ?forceStaticAudio and ?forceClippedAudio belong to audio-issue detection and cannot reach a
 * bad-signal mode, so a dev using one for its original purpose must not find this session's
 * writes suppressed.
 *
 * Any of the params being PRESENT counts, `false` included. `?forceBadSignalCursor=false` closes
 * the branch so there is nothing to skip anyway, and "a human is driving this session by hand" is
 * the property worth detecting, not which way they pointed it.
 *
 * @returns {boolean}
 */
export function isBadSignalForcedSession() {
  return FORCE_BAD_SIGNAL_CURSOR !== undefined
    || FORCE_BAD_SIGNAL_LOG_ONLY !== undefined
    || FORCE_BAD_SIGNAL_AUDIO?.deadSilence === true
    || FORCE_BAD_SIGNAL_AUDIO?.pervasiveClipping === true
    || FORCE_BAD_SIGNAL_NETWORK === true;
}

/**
 * The master gate for the whole TT-991 branch: classification, stashing, the decision and its log.
 * Either opens it — the badSignalCursorEnabled license flag (live) or the BAD_SIGNAL_LOG_ONLY build
 * constant (dark launch, everyone) — and both run the same code down to the single line in
 * computeEligibleBehavior that decides whether the answer is applied. Only an exact true counts on
 * the license flag, so no stray config value can change behavior for a district that never asked
 * for one. Its precedence is the district's own AmiraLicenses record first, appSettings as the
 * fallback, like cursorFreeTutorWCPMThreshold; an explicit district-level false therefore blocks an
 * appSettings true.
 *
 * ?forceBadSignalCursor=true|false overrides both in either direction on every stack so the branch
 * is exercisable in a browser; with the param absent FORCE_BAD_SIGNAL_CURSOR is undefined and the
 * license decides. `false` wins over log-only on purpose — a debug "off" has to mean fully off —
 * and `true` additionally forces the decision to be APPLIED rather than logged, so one param
 * exercises the whole branch. See isBadSignalLogOnly for that precedence.
 *
 * @param {object} [userData] student/district config; defaults to the live userData store
 * @returns {boolean}
 */
export function isBadSignalCursorEnabled(userData = getUserData()) {
  if (FORCE_BAD_SIGNAL_CURSOR !== undefined) {
    return FORCE_BAD_SIGNAL_CURSOR;
  }
  return readOverride(userData || {}, 'badSignalCursorEnabled') === true || isBadSignalLogOnly();
}

/**
 * A synthetic getActivityAudioMetrics() payload for the audio debug params
 * (constants/featureFlags.FORCE_BAD_SIGNAL_AUDIO decides whether it is used at all).
 *
 * Built FROM the thresholds rather than from fixed numbers, so a TT-1230 retune cannot leave the
 * debug switch quietly under the line: the flat run is flatlineMinRunSec exactly and the pervasive
 * counts are pervasiveMinPhrases out of pervasiveMinPhrases, i.e. a 100% share over the smallest
 * denominator the evidence floor accepts.
 *
 * RAW MEASUREMENTS ONLY, no verdict fields. deadSilence/pervasiveClipping used to set #5953's two
 * activity-level booleans directly; the classifier now derives both from the same raw counts the
 * stored path carries, so a payload asserting the verdict would exercise a route production does
 * not have. Only the forced conditions are dirty; everything else is a clean zero -- with one
 * consistency rule: a pervasively clipped phrase (p90 >= 40) is necessarily a clipped one
 * (p90 >= 1), so forcing pervasive clipping raises clippedPhraseCount too. These rows land in the
 * dark-launch log beside measured ones and must not have a shape no real payload can.
 *
 * ONLY deadSilence AND pervasiveClipping CAN PRODUCE hadBadAudio: true, and that is the shipped
 * rule rather than a temporary state. ?forceLowAudio and ?forceClippedAudio move only their
 * measurement — isLowVolume and clippedPhraseCount — and their branches stay null, because
 * TT-1230 conjoins both with a story-word overlap and those two branches are not implemented (see
 * cursor/priorActivitySignals.classifyAudioBranches). ?forceStaticAudio moves nothing at all:
 * TT-1230's table has no static branch and §5 rejected the static family. The three params are kept
 * because they populate the dark-launch log with the measurements the two unimplemented branches
 * would have read; none of them reaches a cursor mode.
 *
 * @param {{lowVolume?: boolean, staticLowBand?: boolean, clipped?: boolean,
 *          deadSilence?: boolean, pervasiveClipping?: boolean}} forced
 * @param {object} [audioThresholds] defaults to the live audio thresholds
 * @returns {object} the field set cursor/priorActivitySignals.fromLiveMetrics reads
 */
export function buildForcedAudioMetrics(forced, audioThresholds = getSignalThresholds().audio) {
  const { flatlineMinRunSec, pervasiveMinPhrases } = audioThresholds || {};
  // A denominator the pervasive evidence floor accepts. Below it the branch answers null, not
  // true, which would look like a broken switch rather than a declined one.
  const phrases = Number.isFinite(pervasiveMinPhrases) && pervasiveMinPhrases > 0 ? pervasiveMinPhrases : 1;
  const totalSegmentCount = 20;

  return {
    totalSegmentCount,
    lowRmsSegmentCount: forced?.lowVolume === true ? totalSegmentCount : 0,
    phrasesWithAudioCount: phrases,
    pervasivelyClippedPhraseCount: forced?.pervasiveClipping === true ? phrases : 0,
    clippedPhraseCount: (forced?.clipped === true || forced?.pervasiveClipping === true) ? phrases : 0,
    flatlineMaxRunSec: forced?.deadSilence === true && Number.isFinite(flatlineMinRunSec)
      ? flatlineMinRunSec
      : 0,
  };
}

/**
 * GT-306's debug stand-in for the login tutor history: one session per required high-RTT day
 * (minHighRttDays, at least 2), each on its own local calendar day and each with one phrase whose
 * ackP95Ms sits exactly on highRttP95Ms, so both halves of TT-1377's rule hold (every day, 100% of
 * sessions) at the live thresholds. Only built when ?forceHighRttNetwork=true; null otherwise, so
 * the caller falls through to the real stash.
 *
 * Dated at local noon on each of the previous N calendar days, never `now` minus multiples of 24h:
 * across a DST fall-back day two instants 24h apart can share a date, and the rule would see one
 * day. The decision log marks the history forced (networkHistory.forced) at the call site.
 *
 * @param {object} [networkThresholds] defaults to the live network thresholds
 * @param {number} [now] epoch ms
 * @returns {Array<object>|null} rows in the shape API.saveUserData stashes as tutorHistorySignals
 */
export function buildForcedNetworkHistory(networkThresholds = getSignalThresholds().network, now = Date.now()) {
  if (FORCE_BAD_SIGNAL_NETWORK !== true) {
    return null;
  }
  const p95 = Number.isFinite(networkThresholds?.highRttP95Ms) ? networkThresholds.highRttP95Ms : 3000;
  const days = Math.max(2, Math.ceil(Number.isFinite(networkThresholds?.minHighRttDays) ? networkThresholds.minHighRttDays : 2));
  const row = (daysAgo) => {
    const date = new Date(now);
    date.setHours(12, 0, 0, 0);
    date.setDate(date.getDate() - daysAgo);
    return {
      createdAt: date.toISOString(),
      activityStatus: 'scored',
      audioNetworkSignals: {
        schemaVersion: 1,
        badCount: 0,
        monitoringActive: true,
        phrases: [{ phraseIndex: 0, clippingP90: 0, ackP50Ms: p95 / 2, ackP95Ms: p95, ackedCount: 6 }],
      },
    };
  };
  return Array.from({ length: days }, (_unused, i) => row(i + 1));
}

/**
 * GT-306's per-signal kill switches: forceBadAudioSignalOff and forceBadNetworkSignalOff. Each
 * stops its signal from making a NEW bad-signal cursor decision; the other signal still can. They
 * are read only by the fresh policy answer, never by recall, so a student already latched into
 * cursor-free or tap-to-read stays there — clearing that is still the CSM team's call.
 *
 * NOT readOverride's precedence. An exact true on EITHER the district's own license or appSettings
 * (district 9999999) switches the signal off, so a platform-wide kill cannot be undone by a
 * district record that happens to carry false. The signals are still classified and logged.
 *
 * The license is read once at login (API.saveUserData), and a session latch outranks a fresh
 * decision anyway, so flipping a switch takes effect on the student's next launch, not mid-session.
 *
 * @param {object} [userData] student/district config; defaults to the live userData store
 * @returns {{audioOff: boolean, networkOff: boolean}}
 */
export function getBadSignalKillSwitches(userData = getUserData()) {
  const config = userData || {};
  const isOff = (key) => config[key] === true || config.appSettings?.[key] === true;
  return {
    audioOff: isOff('forceBadAudioSignalOff'),
    networkOff: isOff('forceBadNetworkSignalOff'),
  };
}

// District-level field first, appSettings (district 9999999) as the fallback.
function readOverride(userData, key) {
  return userData[key] ?? userData.appSettings?.[key];
}

// Only keys the defaults declare, and only finite numbers. An unrecognized key is dropped:
// nothing reads it, and carrying it forward would imply it does something. A JSON string is
// parsed first (the license record may carry the group as text); arrays are not objects here.
// `ranges` optionally names a validity check per key; a finite value that fails it keeps the
// default and is logged, so a typo in a district override cannot quietly rewrite the rule.
function mergeThresholds(defaults, override, ranges = {}) {
  const group = typeof override === 'string' ? parseJsonObject(override) : override;
  if (!group || typeof group !== 'object' || Array.isArray(group)) {
    return defaults;
  }

  const merged = { ...defaults };
  for (const key of Object.keys(defaults)) {
    const value = group[key];
    if (!Number.isFinite(value)) {
      continue;
    }
    if (ranges[key] && !ranges[key](value)) {
      console.warn(`[signalThresholds] ignoring out-of-range threshold override ${key}=${value}; using default`);
      continue;
    }
    merged[key] = value;
  }
  return Object.freeze(merged);
}

// A malformed override is dropped to defaults rather than thrown -- a bad license value must not
// break classification -- but it is logged, not swallowed silently: a district whose
// badSignalAudioThresholds/badSignalNetworkThresholds JSON is malformed would otherwise be
// classified against the in-code defaults with no trace of why its override never took.
function parseJsonObject(text) {
  try {
    return JSON.parse(text);
  } catch (err) {
    console.warn('[signalThresholds] ignoring malformed threshold JSON override; using defaults', err);
    return null;
  }
}
